// @flow
import flatten from 'lodash/flatten';
import moment from 'moment';
import { combineReducers } from 'redux';
import { routerReducer } from 'react-router-redux';

import { reducer as form } from 'redux-form';
import { reducer as auth, selectors as fromAuth } from 'nozzmo-redux-jwt';

import { genSelector } from '../utils';

import login, * as fromLogin from './login';

import type { PermissionsState } from './permissions';
import permissions, * as fromPermissions from './permissions';

import type { BusesState } from './buses';
import buses, * as fromBuses from './buses';

import type { MapState } from './map';
import maps, * as fromMaps from './map';

import type { DriversState } from './drivers';
// import type { DRIVER_TYPE } from '../types/drivers';
import drivers, * as fromDrivers from './drivers';

import type { MonitorsState } from './monitors';
// import type { MONITOR_TYPE } from '../types/monitors';
import monitors, * as fromMonitors from './monitors';

import type { NotificationsState } from './notifications';
import notifications, * as fromNotifications from './notifications';

import type { GatewaysState } from './gateways';
import gateways, * as fromGateways from './gateways';

import type { ReportsState } from './reports';
import reports, * as fromReports from './reports';

import type { GatesState } from './gates';
import gates, * as fromGates from './gates';

import type { ROUTE_STOP_TYPE, ROUTE_TYPE } from '../types/routes';
import type { RoutesState } from './routes';
import routes, * as fromRoutes from './routes';

import type { RouteStopsState } from './routeStops';
import routeStops, * as fromRouteStops from './routeStops';

import type { GatewayUserActionState } from './gatewayUserActions';
import gatewayUserActions, * as fromGatewayUser from './gatewayUserActions';

import type { SchoolsState } from './schools';
import schools, * as fromSchools from './schools';

import type { StudentsState } from './students';
import type { STUDENT_TYPE } from '../types/student';
import students, * as fromStudents from './students';

import type { TripStopsState } from './tripStops';
import tripStops, * as fromTripStops from './tripStops';

import type { TripsState } from './trips';
import trips, * as fromTrips from './trips';

import type { ChangeStopRequestState } from './changeStopRequest';
import changeStopRequests, * as fromChangeStopRequests from './changeStopRequest';

import type { LoginState } from './login';

import type { PhonecoderequestState } from './phoneCodeRequest';
import phoneCodeRequest, * as fromPhoneCodeRequest from './phoneCodeRequest';

import type { FollowingTripState } from './followingTrip';
import followingTrip, * as fromFollowingTrip from './followingTrip';

import type { SocketState } from './socket';
import socket, * as fromSocket from './socket';

import type { TimeState } from './time';
import time, * as fromTime from './time';

import type { NewStopMarkerState } from './newStopMarker';
import newStopMarker, * as fromNewStopMarker from './newStopMarker';

import type { CloseableState } from './closeable';
import closeable, * as fromCloseable from './closeable';

import type { ParentsState } from './parents';
import parents, * as fromParents from './parents';

import type { UsersState } from './users';
import users, * as fromUsers from './users';

import type { TemporaryEventsState } from './temporaryEvents';
import temporaryEvents, * as fromTemporaryEvents from './temporaryEvents';
import type { GATEWAY_USER_ACTION_TYPE } from '../types/gatewayUserActions';

import type { NetworkState } from './network';
import network, * as fromNetwork from './network';


export type AppState = {
  router: Object,
  form: Object,
  _persist: Object,
  auth: {
    token: string,
    decoded: Object,
    error: {
      message: string,
      extra: Object
    }
  },
  login: LoginState,
  permissions: PermissionsState,
  buses: BusesState,
  drivers: DriversState,
  monitors: MonitorsState,
  notifications: NotificationsState,
  gateways: GatewaysState,
  gates: GatesState,
  routes: RoutesState,
  routeStops: RouteStopsState,
  maps: MapState,
  schools: SchoolsState,
  students: StudentsState,
  trips: TripsState,
  tripStops: TripStopsState,
  phoneCodeRequest: PhonecoderequestState,
  followingTrip: FollowingTripState,
  socket: SocketState,
  time: TimeState,
  closeable: CloseableState,
  newStopMarker: NewStopMarkerState,
  parents: ParentsState,
  users: UsersState,
  changeStopRequests: ChangeStopRequestState,
  temporaryEvents: TemporaryEventsState,
  gatewayUserActions: GatewayUserActionState,
  reports: ReportsState,
  network: NetworkState,
};

const reducer = combineReducers({
  router: routerReducer,
  form,
  auth,
  login,
  permissions,
  buses,
  maps,
  drivers,
  monitors,
  notifications,
  routes,
  routeStops,
  schools,
  students,
  trips,
  tripStops,
  phoneCodeRequest,
  followingTrip,
  socket,
  time,
  closeable,
  newStopMarker,
  parents,
  users,
  gateways,
  gates,
  changeStopRequests,
  temporaryEvents,
  gatewayUserActions,
  reports,
  network,
});

export default reducer;

export const getLoginError = (state: AppState) => fromAuth.getError(state.auth);

export const isAuthenticated = genSelector(fromAuth.getIsAuth, 'auth');
export const getUser = genSelector(fromAuth.getDecoded, 'auth');
export const getToken = genSelector(fromAuth.getToken, 'auth');
export const getUsername = (state: AppState) => getUser(state).username;
export const getUserProfilePicture = (
  state: AppState,
) => (getUser(state).profile_picture ? getUser(state).profile_picture.url : undefined);
export const getUserId = (state: AppState) => getUser(state).user_id;
export const getProfiles = (state: AppState) => getUser(state).profiles;
export const getProfilesOfType = (
  state: AppState,
  testType: string,
) => getProfiles(state).filter(({ type }) => type === testType);

export const getProfileTypes = (state: AppState) => getProfiles(state).map(({ type }) => type);
export const hasProfileType = (
  state: AppState,
  testType: string,
) => getProfileTypes(state).includes(testType);
export const getProfileOfType = (state: AppState, testType: string) => {
  if (hasProfileType(state, testType)) {
    return getProfilesOfType(state, testType)[0];
  }

  return undefined;
};


export const hasPermission = genSelector(fromPermissions.hasPermission, 'permissions');
export const getPermissions = genSelector(fromPermissions.getPermissions, 'permissions');

export const isLoginLoading = genSelector(fromLogin.isLoginLoading, 'login');

export const getMapZoom = genSelector(fromMaps.getMapZoom, 'maps');
export const getMapPosition = genSelector(fromMaps.getMapPosition, 'maps');
export const getMapAnimate = genSelector(fromMaps.getMapAnimate, 'maps');
export const isMapPristine = genSelector(fromMaps.isMapPristine, 'maps');
export const isMapLoading = genSelector(fromMaps.isMapLoading, 'maps');
export const getMapLoadingMessage = genSelector(fromMaps.getMapLoadingMessage, 'maps');


export const getBus = genSelector(fromBuses.getBus, 'buses');
export const getBusIsFetching = genSelector(fromBuses.getBusIsFetching, 'buses');
export const getBuses = genSelector(fromBuses.getBuses, 'buses');
export const isBusFetching = genSelector(fromBuses.isBusFetching, 'buses');
export const getBusError = genSelector(fromBuses.getBusError, 'buses');
export const getBusLocation = genSelector(fromBuses.getBusLocation, 'buses');
export const getCurrentBus = genSelector(fromBuses.getCurrentBus, 'buses');
export const getBusFilter = genSelector(fromBuses.getBusSearchFilter, 'buses');

export const getDriver = genSelector(fromDrivers.getDriver, 'drivers');
export const isDriverFetching = genSelector(fromDrivers.isDriverFetching, 'drivers');
export const getDriverError = genSelector(fromDrivers.getDriverError, 'drivers');

export const getMonitor = genSelector(fromMonitors.getMonitor, 'monitors');
export const isMonitorFetching = genSelector(fromMonitors.isMonitorFetching, 'monitors');
export const getMonitorError = genSelector(fromMonitors.getMonitorError, 'monitors');

export const getNotification = genSelector(fromNotifications.getNotification, 'notifications');
export const getNotificationOrder = genSelector(fromNotifications.getNotificationOrder, 'notifications');
export const getNotifications = genSelector(fromNotifications.getNotifications, 'notifications');
export const getNotificationsCount = genSelector(fromNotifications.getNotificationsCount, 'notifications');
export const areNotificationsFetching = genSelector(fromNotifications.areNotificationsFetching, 'notifications');
export const isNotificationFetching = genSelector(fromNotifications.isNotificationFetching, 'notifications');
export const getNotificationsNextPage = genSelector(fromNotifications.getNotificationsNextPage, 'notifications');
export const getNotificationsFetchError = genSelector(fromNotifications.getNotificationsFetchError, 'notifications');
export const getNotificationError = genSelector(fromNotifications.getNotificationError, 'notifications');
export const getCurrentScreen = genSelector(fromNotifications.getCurrentScreen, 'notifications');
export const hasMoreNotifications = genSelector(fromNotifications.hasMoreNotifications, 'notifications');
export const isSendingNotification = genSelector(fromNotifications.isSendingNotification, 'notifications');
export const getNotificationFilters = genSelector(fromNotifications.getNotificationFilters, 'notifications');

export const getRoute = genSelector(fromRoutes.getRoute, 'routes');
export const getRoutes = genSelector(fromRoutes.getRoutes, 'routes');
export const getRoutesById = genSelector(fromRoutes.getRoutesById, 'routes');
export const getRoutesOrder = genSelector(fromRoutes.getRoutesOrder, 'routes');
export const getVisibleRoutes = genSelector(fromRoutes.getVisibleRoutes, 'routes');
export const getRouteStopsIds = genSelector(fromRoutes.getRouteStopsIds, 'routes');
export const getCurrentRoute = genSelector(fromRoutes.getCurrentRoute, 'routes');
export const isFetchingRoutes = genSelector(fromRoutes.isFetchingRoutes, 'routes');
export const getRouteToDelete = genSelector(fromRoutes.getRouteToDelete, 'routes');
export const isRouteFetching = genSelector(fromRoutes.isRouteFetching, 'routes');
export const getRoutesFetchingError = genSelector(fromRoutes.getRoutesFetchingError, 'routes');
export const getSelectedRoute = genSelector(fromRoutes.getSelectedRoute, 'routes');
export const isRouteWatchingStops = genSelector(fromRoutes.isRouteWatchingStops, 'routes');
export const getRoutesError = genSelector(fromRoutes.getRoutesError, 'routes');
// export const getRouteMonitor = (state: AppState, id: number): MONITOR_TYPE => {
//   const route = getRoute(state, id);

//   if(typeof route !== 'undefined') {

//   }
// };

export const getMonitorRoutes = (
  state: AppState,
  id: number,
): Array<ROUTE_TYPE> => {
  const monitor = getMonitor(state, id);
  const result = [];
  if (monitor && monitor.routes) {
    monitor.routes.forEach((route) => {
      const obj = getRoute(state, route);
      if (obj) {
        result.push(obj);
      }
    });
  }
  return result;
};
export const getStudent = genSelector(fromStudents.getStudent, 'students');

export const getRouteStop = genSelector(fromRouteStops.getRouteStop, 'routeStops');
export const getRouteStopIsEditing = genSelector(fromRouteStops.getRouteStopIsEditing, 'routeStops');
export const getRouteStops = (
  state: AppState,
  routeId: number,
): Array<ROUTE_STOP_TYPE> => getRouteStopsIds(
  state,
  routeId,
).map(stopId => getRouteStop(state, stopId)).filter(stop => typeof stop !== 'undefined');
export const getCurrentRouteStopId = genSelector(fromRouteStops.getCurrentRouteStopId, 'routeStops');
export const getCurrentRouteStop = genSelector(fromRouteStops.getCurrentRouteStop, 'routeStops');
export const isRouteStopFetchingStudents = genSelector(fromRouteStops.isRouteStopFetchingStudents, 'routeStops');
export const isRouteStopFetching = genSelector(fromRouteStops.isRouteStopFetching, 'routeStops');
export const getRouteStopError = genSelector(fromRouteStops.getRouteStopError, 'routeStops');
export const getRouteStopStudents = (state: AppState, id: number): Array<STUDENT_TYPE> => {
  const routeStop = getRouteStop(state, id);
  if (typeof routeStop !== 'undefined') {
    return routeStop.students.map(
      sid => getStudent(state, sid),
    ).filter(
      student => typeof student !== 'undefined',
    );
  }

  return [];
};
export const isAssigningStops = genSelector(fromRouteStops.isAssigningStops, 'routeStops');

export const getSchool = genSelector(fromSchools.getSchool, 'schools');
export const getCurrentSchool = genSelector(fromSchools.getCurrentSchool, 'schools');
export const isSchoolFetching = genSelector(fromSchools.isSchoolFetching, 'schools');
export const getSchoolError = genSelector(fromSchools.getSchoolError, 'schools');

export const getStudentRoutesStopsIds = genSelector(fromStudents.getStudentRoutesStops, 'students');
export const getStudentOrder = genSelector(fromStudents.getStudentOrder, 'students');
export const getStudents = genSelector(fromStudents.getStudents, 'students');
export const isFetchingStudents = genSelector(fromStudents.isFetchingStudents, 'students');
export const isStudentFetching = genSelector(fromStudents.isStudentFetching, 'students');
export const isStudentFetchingGateways = genSelector(fromStudents.isStudentFetchingGateways, 'students');
export const isFetchingStudentParents = genSelector(fromStudents.isFetchingStudentParents, 'students');
export const getSelectedStudent = genSelector(fromStudents.getSelectedStudent, 'students');
export const isSavingRouteStopAssignations = genSelector(fromStudents.isSavingRouteStopAssignations, 'students');
export const getStudentsError = genSelector(fromStudents.getStudentsError, 'students');
export const getStudentRoutesStops = (state: AppState, idStudent: number) => {
  const ids = getStudentRoutesStopsIds(state, idStudent);
  if (ids) {
    return ids.map(id => getRouteStop(state, id));
  }
  return [];
};


export const studentHasConfilctedStops = (state: AppState, id: number): boolean => {
  let hasConflict = false;
  const stops = getStudentRoutesStops(state, id);
  if (stops) {
    const routeObjects = stops.map(stop => getRoute(state, stop.route));
    const goingRoutes = routeObjects.filter(r => r.route_type === 'going');
    const goingRoutesIds = goingRoutes.map(r => r.id);
    const returningRoutes = routeObjects.filter(r => r.route_type === 'returning');
    const returningRoutesIds = returningRoutes.map(r => r.id);
    const goingStops = stops.filter(stop => goingRoutesIds.includes(stop.route));
    const returningStops = stops.filter(stop => returningRoutesIds.includes(stop.route));
    const weekDays = [0, 1, 2, 3, 4, 5, 6];
    weekDays.forEach((day) => {
      // Check going stops
      let goingStopsWeekDays = [];
      goingStops.forEach((stop) => {
        goingStopsWeekDays = goingStopsWeekDays.concat(stop.week_days);
      });
      const countGoing = goingStopsWeekDays.reduce(
        (pre, cur) => ((cur === day) ? pre + 1 : pre),
        0,
      );
      if (countGoing >= 2) {
        hasConflict = true;
      }

      // Returning stops
      let returningWeekDays = [];
      returningStops.forEach((stop) => {
        returningWeekDays = returningWeekDays.concat(stop.week_days);
      });
      const countReturning = returningWeekDays.reduce(
        (pre, cur) => ((cur === day) ? pre + 1 : pre),
        0,
      );
      if (countReturning >= 2) {
        hasConflict = true;
      }
    });
  }
  return hasConflict;
};

export const getTripStop = genSelector(fromTripStops.getTripStop, 'tripStops');

export const getTripStopsIds = genSelector(fromTrips.getTripStopsIds, 'trips');
export const getTripStops = (
  state: AppState,
  id: number,
) => fromTrips.getTripStopsIds(state.trips, id).map(i => getTripStop(state, i));

export const isTripStopCompleted = genSelector(fromTripStops.isTripStopCompleted, 'tripStops');
export const getTripStopStudentIds = genSelector(fromTripStops.getTripStopStudentIds, 'tripStops');
export const getTripStopPathToNextStop = genSelector(fromTripStops.getTripStopPathToNextStop, 'tripStops');
export const getTripStopETA = (state: AppState, id: number): number => {
  const stop = getTripStop(state, id);

  if (typeof stop.eta !== 'undefined') {
    return stop.eta;
  }

  const tripStopObjects = getTripStops(state, stop.trip);

  if (tripStopObjects.length > 0) {
    const stopOrdinal = getTripStopsIds(state, stop.trip).indexOf(id);

    if (stopOrdinal > 0) {
      // Avoid the first item, should have eta
      const relevantStopsSlice = tripStopObjects.slice(1, stopOrdinal > 0 ? stopOrdinal + 1 : 0);

      return relevantStopsSlice.reduce(
        ({ accumulated }, { eta, eta_delta = 0 }) => ({
          accumulated: typeof eta !== 'undefined' ? eta : accumulated + eta_delta,
        }),
        { accumulated: tripStopObjects[0].eta || 0 },
      ).accumulated;
    }

    return tripStopObjects[0].eta || 0;
  }

  return 0;
};

export const getTripStopFromRouteStop = (state: AppState, routeStopId: number): any => {
  const tss = fromTripStops.getTripStops(state.tripStops);
  const ts = tss.find(item => item.derived_from === routeStopId);
  if (!ts) {
    return undefined;
  }
  return ts;
};

export const getTrip = genSelector(fromTrips.getTrip, 'trips');
export const getVisibleTrips = genSelector(fromTrips.getVisibleTrips, 'trips');
export const getTripsError = genSelector(fromTrips.getTripsError, 'trips');
export const isTripFetching = genSelector(fromTrips.isTripFetching, 'trips');
export const isFetchingTrips = genSelector(fromTrips.isFetchingTrips, 'trips');
export const isFinishingTrip = genSelector(fromTrips.getIsFinishingTrip, 'trips');
export const getTrips = genSelector(fromTrips.getTrips, 'trips');
export const getRouteActiveTrips = (
  state: AppState,
  routeId: number,
) => getTrips(state).filter(({ route, is_completed }) => route === routeId && !is_completed);
export const isTripOpen = genSelector(fromTrips.isTripOpen, 'trips');
export const isTripCompleted = genSelector(fromTrips.isTripCompleted, 'trips');
export const isTripShowingDetail = genSelector(fromTrips.isTripShowingDetail, 'trips');
export const getTripRouteId = genSelector(fromTrips.getRouteId, 'trips');
export const getTripRoute = (
  state: AppState,
  id: number,
) => getRoute(state.routes, getTripRouteId(state.trips, id));

export const getTripBusId = genSelector(fromTrips.getTripBusId, 'trips');
export const getTripBus = (state: AppState, id: number) => getBus(state, getTripBusId(state, id));
export const getNextTripStop = (state: AppState, id: number) => {
  const pendingStops = getTripStops(state, id).filter(
    tripStop => !isTripStopCompleted(state, tripStop.id),
  );
  if (pendingStops.length > 0) {
    return pendingStops[0];
  }
  return undefined;
};

export const getFirstTripStop = (state: AppState, id: number) => {
  const allStops = getTripStops(state, id);
  if (allStops && allStops.length > 0) {
    return allStops.sort((a, b) => {
      if (a.ordinal < b.ordinal) {
        return -1;
      }
      if (a.ordinal > b.ordinal) {
        return 1;
      }
      return 0;
    })[0];
  }
  return undefined;
};

export const getLastTripDestiny = (state: AppState, id: number) => {
  const stops = getTripStops(state, id);
  return stops[stops.length - 1];
};

export const getTripStopStudents = (state: AppState, stopId: number) => getTripStopStudentIds(
  state,
  stopId,
).map(id => getStudent(state, id)).filter(student => typeof student !== 'undefined');

export const getTripStudents = (state: AppState, id: number): Array<Object> => {
  const tripStopObjects = getTripStops(state, id);
  const tripStudents = tripStopObjects.map(tripStop => getTripStopStudentIds(state, tripStop.id));
  return Array.from(new Set(flatten(tripStudents).map(sid => getStudent(state, sid)).filter(student => typeof student !== 'undefined')));
};

export const getTripStudentAssistance = (
  state: AppState,
  id: number,
): Array<Object> => getTripStudents(state, id).map(
  student => ({
    student: student.id,
    is_present: student.is_present,
  }),
);

export const getTripProgress = (state: AppState, id: number): number => {
  const tSIds = fromTrips.getTripStopsIds(state.trips, id);
  const completedTSIds = tSIds.filter(tsid => isTripStopCompleted(state, tsid));
  return completedTSIds.length / tSIds.length;
};

export const getTripSoftProgress = (state: AppState, id: number): number => {
  const tSIds = fromTrips.getTripStopsIds(state.trips, id);
  const completedTSIds = tSIds.filter(tsid => isTripStopCompleted(state, tsid));
  const progress = (tSIds.length - 0.5 - completedTSIds.length) / (tSIds.length - 1);
  return progress > 0 ? progress : 0;
};

export const getOpenTrips = (
  state: AppState,
): Array<Object> => getTrips(state).filter(trip => isTripOpen(state, trip.id));

export const isAnyTripOpen = (state: AppState): boolean => getOpenTrips(state).length > 0;

export const getShowingDetailTrips = (
  state: AppState,
): Array<Object> => getTrips(state).filter(trip => isTripShowingDetail(state, trip.id));

export const isAnyTripShowingDetail = (
  state: AppState,
): boolean => getShowingDetailTrips(state).length > 0;

export const isPhoneCodeRequestLoading = genSelector(fromPhoneCodeRequest.isPhoneCodeRequestLoading, 'phoneCodeRequest');
export const phoneCodeRequestError = genSelector(fromPhoneCodeRequest.phoneCodeRequestError, 'phoneCodeRequest');

export const isFollowingTrip = genSelector(fromFollowingTrip.isFollowingTrip, 'followingTrip');

export const isSocketOpening = genSelector(fromSocket.isSocketOpening, 'socket');
export const isSocketOpen = genSelector(fromSocket.isSocketOpen, 'socket');
export const isSocketConnected = genSelector(fromSocket.isSocketConnected, 'socket');
export const getSocketError = genSelector(fromSocket.getSocketError, 'socket');
export const getChannel = genSelector(fromSocket.getChannel, 'socket');
export const isChannelJoining = genSelector(fromSocket.isChannelJoining, 'socket');
export const isChannelTimedout = genSelector(fromSocket.isChannelTimedout, 'socket');
export const isChannelJoined = genSelector(fromSocket.isChannelJoined, 'socket');
export const getChannelError = genSelector(fromSocket.getChannelError, 'socket');

export const getCurrentTimestamp = genSelector(fromTime.getCurrentTimestamp, 'time');

export const getCloseable = genSelector(fromCloseable.getCloseable, 'closeable');
export const getIsCloseableOpen = (
  state: AppState,
  id: string,
) => fromCloseable.getIsOpen(getCloseable(state, id));

export const isNewStopMarkerPlaced = genSelector(fromNewStopMarker.isNewStopMarkerPlaced, 'newStopMarker');
export const getNewStopMarkerPosition = genSelector(fromNewStopMarker.getNewStopMarkerPosition, 'newStopMarker');

export const getParent = genSelector(fromParents.getParent, 'parents');
export const isParentFetching = genSelector(fromParents.isParentFetching, 'parents');
export const getParentError = genSelector(fromParents.getParentError, 'parents');

export const isFetchingChangeStopRequest = genSelector(fromChangeStopRequests.isFetchingChangeStopRequest, 'changeStopRequests');
export const getChangeStopRequest = genSelector(fromChangeStopRequests.getChangeStopRequest, 'changeStopRequests');
export const getChangeStopRequests = genSelector(fromChangeStopRequests.getChangeStopRequests, 'changeStopRequests');
export const isChangeStopRequestfetching = genSelector(fromChangeStopRequests.isChangeStopRequestfetching, 'changeStopRequests');
export const isStopRequestFetching = genSelector(fromChangeStopRequests.isChangingStateRequest, 'changeStopRequests');
export const getChangeStopRequestFetchingError = genSelector(fromChangeStopRequests.getChangeStopRequestFetchingError, 'changeStopRequests');
export const getChangeStopRequestIds = genSelector(fromChangeStopRequests.getChangeStopRequestIds, 'changeStopRequests');
export const getStateTypeChangeStopRequests = genSelector(fromChangeStopRequests.getStateTypeChangeStopRequests, 'changeStopRequests');
export const hasMoreChangeStopRequests = genSelector(fromChangeStopRequests.hasMoreChangeStopRequests, 'changeStopRequests');
export const areChangeStopRequestsFetching = genSelector(fromChangeStopRequests.areChangeStopRequestsFetching, 'changeStopRequests');
export const getChangeStopRequestPendingCounter = genSelector(fromChangeStopRequests.getChangeStopRequestPendingCounter, 'changeStopRequests');

export const getOneUser = genSelector(fromUsers.getOneUser, 'users');
export const getProfilesFromSchool = (
  state: AppState,
  userId: number,
  schoolId: number,
) => getOneUser(state).profiles.filter(elm => elm.school === schoolId);
export const getProfilesOfTypeFromSchool = (
  state: AppState,
  userId: number,
  testType: string,
  schoolId: number,
) => {
  const user = getOneUser(state, userId);
  const profiles = user.profiles.filter(
    ({ school, type }) => (school === schoolId && type === testType),
  );
  if (profiles.length > 0) {
    return profiles[0];
  }
  return undefined;
};

export const getUserOrder = genSelector(fromUsers.getUserOrder, 'users');
export const getUsers = genSelector(fromUsers.getUsers, 'users');
export const isUsersFetching = genSelector(fromUsers.isUsersFetching, 'users');
export const getUserNextPage = genSelector(fromUsers.getUserNextPage, 'users');
export const getUsersFetchError = genSelector(fromUsers.getUsersFetchError, 'users');
export const getUserError = genSelector(fromUsers.getUserError, 'users');
export const getUserSelected = genSelector(fromUsers.getUserSelected, 'users');
export const hasMoreUsers = genSelector(fromUsers.hasMoreUsers, 'users');
export const getUserFilters = genSelector(fromUsers.getUserFilters, 'users');
export const getCurrentCreateUsersStep = genSelector(fromUsers.getCurrentCreateUsersStep, 'users');
export const getUsersError = genSelector(fromUsers.getUsersError, 'users');

export const getOneUploadingUser = genSelector(fromUsers.getOneUploadingUser, 'users');
export const getUploadingUsers = genSelector(fromUsers.getUploadingUsers, 'users');
export const getUploadingErrors = genSelector(fromUsers.getUploadingErrors, 'users');
export const isUploadingUsers = genSelector(fromUsers.isUploadingUsers, 'users');
export const isCreatingUser = genSelector(fromUsers.isCreatingUser, 'users');
export const getErrorUserCreating = genSelector(fromUsers.getErrorUserCreating, 'users');

export const getTemporaryEvent = genSelector(fromTemporaryEvents.getTemporaryEvent, 'temporaryEvents');
export const getTemporaryEvents = genSelector(fromTemporaryEvents.getTemporaryEvents, 'temporaryEvents');
export const getActiveTemporaryEvents = genSelector(fromTemporaryEvents.getActiveTemporaryEvents, 'temporaryEvents');
export const getTemporaryEventsByGroup = genSelector(fromTemporaryEvents.getTemporaryEventsByGroup, 'temporaryEvents');
export const getActiveTemporaryEventsByGroup = genSelector(fromTemporaryEvents.getActiveTemporaryEventsByGroup, 'temporaryEvents');

// Gateways
export const getGateways = genSelector(fromGateways.getGateways, 'gateways');
export const getGateway = genSelector(fromGateways.getGateway, 'gateways');
export const getGatewayPage = genSelector(fromGateways.getGatewayPage, 'gateways');
export const areGatewaysFetching = genSelector(fromGateways.areGatewaysFetching, 'gateways');
export const hasMoreGateways = genSelector(fromGateways.hasMoreGateways, 'gateways');
export const getGatewaysChangeStopAvailable = genSelector(fromGateways.getGatewaysChangeStopAvailable, 'gateways');
export const getCurrentGateway = genSelector(fromGateways.getCurrentGateway, 'gateways');
// Gates
export const getGate = genSelector(fromGates.getGate, 'gates');
export const getGates = genSelector(fromGates.getGates, 'gates');
export const areGatesFetching = genSelector(fromGates.areGatesFetching, 'gates');
export const getGatesChangeStopAvailable = genSelector(fromGates.getGatesChangeStopAvailable, 'gates');
export const getCurrentGate = genSelector(fromGates.getCurrentGate, 'gates');

// GatewayUserActions
export const areGatewayUsersFetching = genSelector(fromGatewayUser.areGatewayUsersFetching, 'gatewayUserActions');
export const getGatewayUserAction = genSelector(fromGatewayUser.getGatewayUserAction, 'gatewayUserActions');
export const getGatewayUserActionIsFetching = genSelector(fromGatewayUser.getGatewayUserActionIsFetching, 'gatewayUserActions');

export const hasMoreGatewayUsers = (state: AppState, id: number) => {
  const hasMoreGatewaysOne = hasMoreGateways(state, id);
  const isFetching = areGatewayUsersFetching(state);

  return hasMoreGatewaysOne && !isFetching;
}

export const getStudentGatewayUserActions = (
  state: AppState,
  idStudent: number,
): Array<GATEWAY_USER_ACTION_TYPE> => {
  const student = getStudent(state, idStudent);
  if (student.gatewayUserActions) {
    const gatewayUserActionsObjs = student.gatewayUserActions.map(
      id => getGatewayUserAction(state, id),
    );
    return gatewayUserActionsObjs.map(obj => ({ ...obj, gateway: getGateway(state, obj.gateway) }));
  }
  return [];
};

export const getGatewayUsers = (
  state: AppState,
  idGateway: number,
): Array<GATEWAY_USER_ACTION_TYPE> => {
  const gateway = getGateway(state, idGateway);
  if (gateway) {
    const res = [];
    gateway.users.forEach((elm) => {
      const user = getOneUser(state, elm);
      if (user) {
        res.push(user);
      }
    });
    return res;
  }
  return [];
};
export const getStudentParents = (state: AppState, idStudent: number) => {
  const student = getStudent(state, idStudent);
  const result = [];
  if (student && student.parents) {
    student.parents.forEach((id) => {
      const parent = getParent(state, id);
      if (parent) {
        result.push(parent);
      }
    });
  }
  return result;
};

export const getParentStudents = (state: AppState, idParent: number) => {
  const all = getStudents(state);
  const filtered = all.filter(student => student.parents.includes(idParent));
  return filtered;
};

export const getStudentChangeRequests = (state: AppState, idStudent: number) => {
  const student = getStudent(state, idStudent);
  const result = [];
  if (student && student.changeStopRequests) {
    student.changeStopRequests.forEach((id) => {
      const change_request = getChangeStopRequest(state, id);
      const toGateway = getGateway(state, change_request.to_gateway);
      change_request.toGatewayObj = { ...toGateway };
      if (toGateway) {
        if (toGateway.allowed_actions.filter(t => t === 'entrance').length > 0
          && toGateway.allowed_actions.filter(t => t === 'exit').length > 0) {
          toGateway.type = 'Entrada / Salida';
        } else if (toGateway.allowed_actions.filter(t => t === 'entrance').length > 0) {
          toGateway.type = 'Entrada';
        } else if (toGateway.allowed_actions.filter(t => t === 'exit').length > 0) {
          toGateway.type = 'Salida';
        }
        if (change_request) {
          result.push(change_request);
        }
      }
    });
  }
  return result;
};
/* export const getStudentChangeRequests = (state: AppState, idStudent: number) => {
  const currentState = getStateTypeChangeStopRequests(state);
  const changeRequests: any[] = getChangeStopRequests(state, currentState);
  return changeRequests
    .filter(request => request.student === idStudent)
    .map(request => ({
      ...request,
      
    }));
}; */
// Reports
export const getCurrentReportKey = genSelector(fromReports.getCurrentReportKey, 'reports');
export const getReportData = genSelector(fromReports.getReportData, 'reports');

export const getPersist = (state: AppState) => state._persist;

export const isNetworkConnected = genSelector(fromNetwork.isConnected, 'network');



// WEBPACK FOOTER //
// ./src/scripts/reducers/index.js